// src/pages/stockAdjustment/StockAdjustmentListPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Package,
  Plus,
  Eye,
  Download,
  TrendingUp,
  TrendingDown,
  Calendar,
  Warehouse as WarehouseIcon
} from 'lucide-react';
import {
  PageHeader,
  DataTable,
  FilterPanel
} from '../../components/common';
import {
  getAllStockAdjustments,
  getUserWarehouses
} from '../../services/api/stockAdjustment';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatDate } from '../../utils/dateUtils';
import styles from './StockAdjustmentListPage.module.css';

const StockAdjustmentListPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();

  // State management
  const [adjustments, setAdjustments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [20, 50, 100]
  });

  // Sorting state
  const [sorting, setSorting] = useState({
    orderBy: 'createdAt',
    orderDirection: 'DESC'
  });

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    warehouseId: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  });

  // Reference data for filters
  const [referenceData, setReferenceData] = useState({
    warehouses: []
  });

  // Bulk actions
  const [selectedAdjustments, setSelectedAdjustments] = useState([]);

  // Status configuration
  const statusConfig = {
    draft: {
      label: t('stockAdjustment.statusDraft'),
      color: '#f59e0b',
      bgColor: '#fef3c7'
    },
    finalized: {
      label: t('stockAdjustment.statusFinalized'),
      color: '#10b981',
      bgColor: '#d1fae5'
    }
  };

  // Load reference data on mount
  useEffect(() => {
    loadReferenceData();
  }, []);

  // Load adjustments when filters or pagination/sorting changes
  useEffect(() => {
    fetchAdjustments();
  }, [pagination.currentPage, pagination.pageSize, sorting.orderBy, sorting.orderDirection, filters]);

  const loadReferenceData = async () => {
    try {
      const warehousesData = await getUserWarehouses();
      setReferenceData({
        warehouses: warehousesData.warehouses || []
      });
    } catch (err) {
      console.error('Failed to load reference data:', err);
    }
  };

  const fetchAdjustments = async () => {
    try {
      setIsLoading(true);

      const params = {
        page: pagination.currentPage,
        limit: pagination.pageSize,
        orderBy: sorting.orderBy,
        orderDirection: sorting.orderDirection
      };

      // Add filters
      if (filters.search) params.search = filters.search;
      if (filters.warehouseId) params.warehouseId = filters.warehouseId;
      if (filters.status) params.status = filters.status;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;

      const response = await getAllStockAdjustments(params);
      setAdjustments(response.adjustments || []);
      setPagination(prev => ({
        ...prev,
        total: response.total || 0
      }));

    } catch (err) {
      console.error('Failed to fetch adjustments:', err);
      if (err.response?.status === 403) {
        showError(t('errors.permissionDenied'));
      } else {
        showError(t('stockAdjustment.failedToLoadAdjustments'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSearchChange = (searchValue) => {
    setFilters(prev => ({ ...prev, search: searchValue }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({
      ...prev,
      pageSize: newPageSize,
      currentPage: 1
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      warehouseId: '',
      status: '',
      dateFrom: '',
      dateTo: ''
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const hasActiveFilters = () => {
    return Object.values(filters).some(value => value !== '');
  };

  const handleExport = () => {
    // Create CSV content
    const headers = [
      t('stockAdjustment.adjustmentNumber'),
      t('stockAdjustment.warehouse'),
      t('stockAdjustment.adjustmentDate'),
      t('stockAdjustment.totalItems'),
      t('stockAdjustment.totalVariance'),
      t('stockAdjustment.status'),
      t('stockAdjustment.createdBy'),
      t('stockAdjustment.finalizedBy'),
      t('common.created')
    ];
    
    const rows = adjustments.map(adj => [
      adj.adjustmentNumber,
      adj.Warehouse?.name || t('common.na'),
      formatDate(adj.adjustmentDate),
      adj.totalItems || 0,
      adj.totalVariance || 0,
      statusConfig[adj.status]?.label || adj.status,
      adj.createdBy?.name || t('common.na'),
      adj.finalizedBy?.name || '-',
      formatDate(adj.createdAt)
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stock-adjustments-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getVarianceDisplay = (variance) => {
    const value = parseFloat(variance);
    if (value === 0) {
      return { text: '0', icon: null, color: '#6b7280' };
    } else if (value > 0) {
      return { text: `+${value}`, icon: TrendingUp, color: '#16a34a' };
    } else {
      return { text: value.toString(), icon: TrendingDown, color: '#dc2626' };
    }
  };

  // Define filter configuration
  const filterDefinitions = [
    {
      key: 'warehouseId',
      label: t('stockAdjustment.warehouse'),
      type: 'select',
      icon: <WarehouseIcon size={18} />,
      placeholder: t('stockAdjustment.allWarehouses'),
      options: referenceData.warehouses.map(w => ({
        value: w.id.toString(),
        label: w.name
      }))
    },
    {
      key: 'status',
      label: t('stockAdjustment.status'),
      type: 'select',
      placeholder: t('stockAdjustment.allStatuses'),
      options: Object.entries(statusConfig).map(([key, config]) => ({
        value: key,
        label: config.label
      }))
    },
    {
      key: 'date',
      label: t('stockAdjustment.dateRange'),
      type: 'daterange',
      gridColumn: 'span 2'
    }
  ];

  // Define table columns
  const columns = [
    {
      key: 'adjustmentNumber',
      header: t('stockAdjustment.adjustmentNumber'),
      sortable: true,
      width: '180px',
      render: (row) => (
        <div className={styles.adjustmentNumber}>{row.adjustmentNumber}</div>
      )
    },
    {
      key: 'warehouse',
      header: t('stockAdjustment.warehouse'),
      width: '180px',
      render: (row) => (
        <div className={styles.warehouseCell}>
          <WarehouseIcon size={14} />
          {row.Warehouse?.name || t('common.na')}
        </div>
      )
    },
    {
      key: 'adjustmentDate',
      header: t('stockAdjustment.adjustmentDate'),
      sortable: true,
      width: '140px',
      render: (row) => (
        <div className={styles.dateCell}>
          <Calendar size={14} />
          {formatDate(row.adjustmentDate)}
        </div>
      )
    },
    {
      key: 'totalItems',
      header: t('stockAdjustment.totalItems'),
      sortable: true,
      width: '120px',
      align: 'center',
      render: (row) => (
        <div className={styles.itemsCount}>
          <Package size={14} />
          {row.totalItems || 0}
        </div>
      )
    },
    {
      key: 'totalVariance',
      header: t('stockAdjustment.totalVariance'),
      sortable: true,
      width: '140px',
      align: 'center',
      render: (row) => {
        const varianceDisplay = getVarianceDisplay(row.totalVariance);
        const VarianceIcon = varianceDisplay.icon;
        return (
          <div className={styles.varianceCell} style={{ color: varianceDisplay.color }}>
            {VarianceIcon && <VarianceIcon size={16} />}
            <strong>{varianceDisplay.text}</strong>
          </div>
        );
      }
    },
    {
      key: 'status',
      header: t('stockAdjustment.status'),
      width: '130px',
      render: (row) => {
        const statusInfo = statusConfig[row.status] || statusConfig.draft;
        return (
          <div 
            className={styles.statusBadge}
            style={{
              backgroundColor: statusInfo.bgColor,
              borderColor: statusInfo.color,
              color: statusInfo.color
            }}
          >
            {statusInfo.label}
          </div>
        );
      }
    },
    {
      key: 'createdBy',
      header: t('stockAdjustment.createdBy'),
      width: '150px',
      render: (row) => row.createdBy?.name || t('common.na')
    },
    {
      key: 'finalizedBy',
      header: t('stockAdjustment.finalizedBy'),
      width: '150px',
      render: (row) => row.finalizedBy?.name || '-'
    }
  ];

  // Define table actions
  const tableActions = {
    render: (row) => (
      <div className={styles.actionsWrapper}>
        <Link
          to={`/inventory/stock-adjustments/${row.id}`}
          className={`${styles.actionButton} ${styles.viewButton}`}
          title={t('common.view')}
        >
          <Eye size={16} />
        </Link>

        <button
          onClick={(e) => {
            e.stopPropagation();
            // Export single adjustment logic here if needed
          }}
          className={`${styles.actionButton} ${styles.exportButton}`}
          title={t('common.export')}
        >
          <Download size={16} />
        </button>
      </div>
    ),
    width: '120px',
    frozen: true
  };

  return (
    <div className={styles.stockAdjustmentListPage}>
      {/* Page Header */}
      <PageHeader
        title={t('stockAdjustment.allStockAdjustments')}
        description={t('stockAdjustment.pageDescription')}
        icon={Package}
        actions={
          hasPermission('create_stock_adjustment') && (
            <Link to="/inventory/stock-adjustments/create" className={styles.primaryButton}>
              <Plus size={20} />
              {t('stockAdjustment.createStockAdjustment')}
            </Link>
          )
        }
      />

      {/* Search and Filters Row */}
      <FilterPanel
        filters={filterDefinitions}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        onExport={handleExport}
        showExport={true}
        exportButtonText={t('common.export')}
        searchConfig={{
          show: true,
          value: filters.search,
          onChange: handleSearchChange,
          placeholder: t('stockAdjustment.searchPlaceholder')
        }}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: handlePageSizeChange,
          options: pagination.pageSizeOptions
        }}
      />

      {/* Data Table */}
      <DataTable
        data={adjustments}
        columns={columns}
        actions={tableActions}
        selectable
        selectedRows={selectedAdjustments}
        onSelectionChange={setSelectedAdjustments}
        onRowClick={(row) => navigate(`/inventory/stock-adjustments/${row.id}`)}
        sorting={sorting}
        onSortChange={setSorting}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={isLoading}
        emptyMessage={
          hasActiveFilters() 
            ? t('stockAdjustment.noAdjustmentsFound')
            : t('stockAdjustment.noAdjustmentsYet')
        }
        emptyDescription={
          hasActiveFilters()
            ? t('stockAdjustment.noAdjustmentsFoundDescription')
            : t('stockAdjustment.noAdjustmentsYetDescription')
        }
        emptyIcon={<Package size={64} />}
        emptyAction={
          hasActiveFilters() ? (
            <button onClick={() => {
              handleClearFilters();
            }} className={styles.primaryButton}>
              {t('common.clearFilters')}
            </button>
          ) : hasPermission('create_stock_adjustment') ? (
            <Link to="/inventory/stock-adjustments/create" className={styles.primaryButton}>
              <Plus size={18} />
              {t('stockAdjustment.createFirstAdjustment')}
            </Link>
          ) : null
        }
      />
    </div>
  );
};

export default StockAdjustmentListPage;